/**
 * GP HTL Reports Page
 * Main page for GP HTL Ventures LLC partnership reporting
 * Provides Dashboard, Deals, Payment Chain, and Revenue & Equity views
 */
import React from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Breadcrumbs,
  Link,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as DealsIcon,
  AccountTree as PaymentChainIcon,
  TrendingUp as RevenueIcon,
  Home as HomeIcon,
  AccountBalance as AccountingIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { GphtlDashboardTab } from './tabs/GphtlDashboardTab';
import { GphtlDealsTab } from './tabs/GphtlDealsTab';
import { GphtlPaymentChainTab } from './tabs/GphtlPaymentChainTab';
import { GphtlRevenueTab } from './tabs/GphtlRevenueTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`gphtl-tabpanel-${index}`}
      aria-labelledby={`gphtl-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `gphtl-tab-${index}`,
    'aria-controls': `gphtl-tabpanel-${index}`,
  };
}

// Map paths to tab indices
const pathToTabIndex: Record<string, number> = {
  '/admin/hub/accounting/gphtl': 0,
  '/admin/hub/accounting/gphtl/deals': 1,
  '/admin/hub/accounting/gphtl/payment-chain': 2,
  '/admin/hub/accounting/gphtl/revenue': 3,
};

const tabIndexToPath: Record<number, string> = {
  0: '/admin/hub/accounting/gphtl',
  1: '/admin/hub/accounting/gphtl/deals',
  2: '/admin/hub/accounting/gphtl/payment-chain',
  3: '/admin/hub/accounting/gphtl/revenue',
};

export const GphtlReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current tab from URL
  const currentTab = pathToTabIndex[location.pathname] ?? 0;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    navigate(tabIndexToPath[newValue]);
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          href="/admin/hub"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/hub');
          }}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Admin Hub
        </Link>
        <Link
          color="inherit"
          href="/admin/hub/accounting"
          onClick={(e) => {
            e.preventDefault();
            navigate('/admin/hub/accounting');
          }}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <AccountingIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Accounting
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          GP HTL Reports
        </Typography>
      </Breadcrumbs>

      {/* Page Title */}
      <Typography variant="h4" gutterBottom>
        GP HTL Reports
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track GP HTL Ventures LLC partnership deals, invoices, payment chains, and equity milestones.
      </Typography>

      <Paper sx={{ width: '100%' }}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            aria-label="GP HTL reports tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<DashboardIcon />}
              iconPosition="start"
              label="Dashboard"
              {...a11yProps(0)}
            />
            <Tab
              icon={<DealsIcon />}
              iconPosition="start"
              label="Deals"
              {...a11yProps(1)}
            />
            <Tab
              icon={<PaymentChainIcon />}
              iconPosition="start"
              label="Payment Chain"
              {...a11yProps(2)}
            />
            <Tab
              icon={<RevenueIcon />}
              iconPosition="start"
              label="Revenue & Equity"
              {...a11yProps(3)}
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          <TabPanel value={currentTab} index={0}>
            <GphtlDashboardTab />
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <GphtlDealsTab />
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            <GphtlPaymentChainTab />
          </TabPanel>
          <TabPanel value={currentTab} index={3}>
            <GphtlRevenueTab />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default GphtlReportsPage;
